import { ProductV2, ProductV2Variation } from '../models/product-v2.model';

export type CampaignFlowType = 'PROSPECT' | 'MOVEL' | 'FIXA';

export interface CampaignProductRef {
  productUuid: string;
  variationsUuid: string[];
  description?: string | null;
  variationDescriptions?: Record<string, string> | null;
}

export interface ActiveCampaign {
  uuid: string;
  code: string;
  status: 'ACTIVE';
  flowTypes: CampaignFlowType[];
  products: CampaignProductRef[];
}

const FLOW_TYPES: ReadonlySet<string> = new Set(['PROSPECT', 'MOVEL', 'FIXA']);

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isFlowType(value: unknown): value is CampaignFlowType {
  return typeof value === 'string' && FLOW_TYPES.has(value);
}

function isProductRef(value: unknown): value is CampaignProductRef {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const product = value as Record<string, unknown>;
  return (
    isNonEmptyString(product['productUuid']) &&
    Array.isArray(product['variationsUuid']) &&
    product['variationsUuid'].length > 0 &&
    product['variationsUuid'].every(isNonEmptyString)
  );
}

export function isActiveCampaign(body: unknown): body is ActiveCampaign {
  if (!body || typeof body !== 'object') {
    return false;
  }

  const record = body as Record<string, unknown>;
  return (
    record['status'] === 'ACTIVE' &&
    isNonEmptyString(record['uuid']) &&
    isNonEmptyString(record['code']) &&
    Array.isArray(record['flowTypes']) &&
    record['flowTypes'].length > 0 &&
    record['flowTypes'].every(isFlowType) &&
    Array.isArray(record['products']) &&
    record['products'].length > 0 &&
    record['products'].every(isProductRef)
  );
}

export function isEnablingSuccess(campaign: ActiveCampaign, productUuid: string): boolean {
  return campaign.products.some(product => product.productUuid === productUuid);
}

/** `null` means no campaign filter. An empty list means the campaign allows no products. */
export function productsForCampaign<T extends { uuid: string }>(
  products: T[],
  productUuids: readonly string[] | null
): T[] {
  if (!productUuids) {
    return products;
  }

  const allowed = new Set(productUuids);
  return (products ?? []).filter(product => allowed.has(product.uuid));
}

/** Keeps campaign products and, on each of them, only the response variations. */
export function catalogForCampaign<T extends { uuid: string; variations?: { uuid: string }[] }>(
  products: T[],
  refs: readonly CampaignProductRef[] | null
): T[] {
  if (!refs) {
    return products;
  }

  const byProduct = new Map(refs.map(ref => [ref.productUuid, new Set(ref.variationsUuid)]));
  return (products ?? []).flatMap(product => {
    const allowed = byProduct.get(product.uuid);
    if (!allowed) {
      return [];
    }
    if (!product.variations) {
      return [product];
    }
    return [{
      ...product,
      variations: product.variations.filter(variation => allowed.has(variation.uuid)),
    }];
  });
}

/** `null` means the category list stays as it is. */
export function categoriesForCampaign<T extends { uuid?: string }>(
  categories: T[],
  categoryUuids: readonly string[] | null
): T[] {
  if (!categoryUuids) {
    return categories;
  }

  const allowed = new Set(categoryUuids);
  return (categories ?? []).filter(category => !!category.uuid && allowed.has(category.uuid));
}

/**
 * Variations of this product that belong to the campaign.
 * `null` when there is no campaign scope, or the product is outside it:
 * the caller keeps the product's own variations.
 */
export function campaignVariations(
  product: { uuid: string; variations?: ProductV2Variation[] | null },
  refs: readonly CampaignProductRef[] | null
): ProductV2Variation[] | null {
  if (!refs || !product?.uuid) {
    return null;
  }

  const ref = refs.find(item => item.productUuid === product.uuid);
  if (!ref) {
    return null;
  }

  const allowed = new Set(ref.variationsUuid);
  return (product.variations ?? []).filter(variation => allowed.has(variation.uuid));
}

export function variationsForCampaign(
  product: ProductV2,
  campaign: ActiveCampaign
): ProductV2Variation[] {
  return campaignVariations(product, campaign.products) ?? [];
}

/** Campaign copy for the product. `null` keeps the catalog description. */
export function campaignProductDescription(
  campaign: ActiveCampaign,
  productUuid: string
): string | null {
  const ref = campaign.products.find(product => product.productUuid === productUuid);
  return campaignText(ref?.description);
}

/** Campaign copy for one variation. `null` keeps the catalog excerpt. */
export function campaignVariationDescription(
  campaign: ActiveCampaign,
  productUuid: string,
  variationUuid: string
): string | null {
  const ref = campaign.products.find(product => product.productUuid === productUuid);
  if (!ref || !ref.variationsUuid.includes(variationUuid)) {
    return null;
  }
  const descriptions = ref.variationDescriptions;
  if (!descriptions || typeof descriptions !== 'object') {
    return null;
  }
  return campaignText(descriptions[variationUuid]);
}

function campaignText(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const text = value.trim().slice(0, 500);
  return text || null;
}
